import { BuildingLibraryIcon, GlobeAltIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

// A reusable component for mission/vision cards
const InfoCard = ({ icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/50 mb-4">
            {icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            {children}
        </p>
    </div>
);

export default function AboutPage() {
    const brandColor = '#e46809';

    return (
        <div className="bg-gray-50 dark:bg-gray-900">
            {/* --- Header Section --- */}
            <header className="bg-white dark:bg-gray-800 shadow-md">
                <div className="container mx-auto px-4 py-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">About Us</h1>
                    <p className="mt-4 text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto">
                        Empowering the visually impaired with the technology to achieve independence.
                    </p>
                </div>
            </header>
            
            <main className="container mx-auto px-4 py-16">
                {/* --- Introduction Section --- */}
                <section className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-white" style={{ color: brandColor }}>
                        Powered by Accesstech Innovation and Research Center
                    </h2>
                   <p className="mt-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
    <strong className="font-bold text-gray-900 dark:text-white">GiveTech totheBlind</strong> is a flagship initiative of the <strong className="font-bold text-gray-900 dark:text-white">Accesstech Innovation and Research Center</strong>, a non-profit organization dedicated to leveraging technology to break down barriers for individuals with visual impairments. We believe that access to the right tools is not a luxury, but a fundamental right that unlocks education, employment, and full participation in society.
</p>
                    <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                        Our platform was born from a simple idea: to create a direct bridge between generous donors and the specific, verified needs of the blind and visually impaired community. We eliminate the guesswork in giving, ensuring that every contribution directly funds a piece of life-changing assistive technology for someone who needs it most.
                    </p>
                </section>

                {/* --- Mission & Vision Section --- */}
                <section className="mt-20">
                    <div className="grid md:grid-cols-3 gap-8">
                        <InfoCard 
                            icon={<BuildingLibraryIcon className="w-8 h-8 text-orange-500" />}
                            title="Our Mission"
                        >
                            To provide transparent and direct access to essential assistive technology for visually impaired individuals, fostering independence and equal opportunity through community-powered giving.
                        </InfoCard>
                        <InfoCard 
                            icon={<GlobeAltIcon className="w-8 h-8 text-orange-500" />}
                            title="Our Vision"
                        >
                            A world where visual impairment is not a barrier to education, employment, or personal fulfillment, and where technology is a great equalizer for all.
                        </InfoCard>
                        <InfoCard 
                            icon={<LightBulbIcon className="w-8 h-8 text-orange-500" />}
                            title="Our Approach"
                        >
                            We partner with professionals to verify the needs of each recipient. By focusing on specific, tangible products, we ensure every donation has a direct and measurable impact on someone's life.
                        </InfoCard>
                    </div>
                </section>

                {/* --- Call to Action Section --- */}
                <section className="mt-20 text-center">
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-white">Join Our Mission</h2>
                    <p className="mt-4 text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                        Whether you are a donor, a potential recipient, or a volunteer, you have the power to change a life. Explore our active campaigns and become a part of our story today.
                    </p>
                    <Link
                        to="/campaigns"
                        className="mt-8 inline-block py-4 px-10 text-white font-bold text-lg rounded-full transition-transform transform hover:scale-105"
                        style={{ backgroundColor: brandColor }}
                    >
                        View Active Campaigns
                    </Link>
                </section>
            </main>
        </div>
    );
}